/* eslint-disable react/jsx-max-depth */
/* eslint-disable react/jsx-handler-names */
import { commonMessages } from '@/__scripts/common.messages';
import { T } from '@/components/i18n/message.i18n.component';
import { Loader } from '@/components/ui/loader/loader.component';
import { Pill } from '@/components/ui/pill/pill.component';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select/select.component';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs/tabs.component';
import { DesignObligation, MaturityStage } from '@/gql/graphql';
import { SortByEnum, SortedBuildingBlock } from '@/hooks/use-building-blocks/use-building-blocks.hook.core';
import { pretailoringPageMessages } from '@/pages/products/pre-tailoring/pre-tailoring.messages';
import { useIntl } from 'react-intl';

type Props = {
  sort: 'DO' | 'MTS';
  loading?: boolean;
  onTabChange?: (context: 'included' | 'excluded') => void;
  handleSortChange: (sort: SortByEnum) => void;
  includedBuildingBlocks: SortedBuildingBlock<DesignObligation | MaturityStage>;
  excludedBuildingBlocks: SortedBuildingBlock<DesignObligation | MaturityStage>;
};

export const BuildingBlocksHeading = ({
  sort,
  loading,
  onTabChange,
  handleSortChange,
  includedBuildingBlocks,
  excludedBuildingBlocks,
}: Props) => {
  const t = useIntl();
  return (
    <div className="flex flex-col gap-4">
      <Tabs className="flex flex-col gap-y-4" defaultValue="included">
        <div className="flex flex-row justify-between items-center">
          <div data-testid="building-blocks-sort-by" className="flex flex-row items-center gap-x-4">
            <p className="typo-primary-400" data-testid="building-blocks-sort-by-text">
              <T {...commonMessages.groupBy} />
            </p>
            <Select
              value={sort}
              data-testid="group-by-select"
              onValueChange={(v) => {
                handleSortChange(v as SortByEnum);
              }}
            >
              <SelectTrigger data-testid="group-by-trigger" className="w-[200px]">
                <SelectValue
                  data-testid="group-by-value"
                  placeholder={t.formatMessage(pretailoringPageMessages.questionFilterPlaceholder)}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem data-testid="group-by-option-mts" value="MTS">
                  <Pill variant="ms" text={t.formatMessage(commonMessages.maturityStage)} />
                </SelectItem>
                <SelectItem data-testid="group-by-option-do" value="DO">
                  <Pill variant="do" text={t.formatMessage(commonMessages.designObligation)} />
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {loading && <Loader className="w-4 h-4" />}
        </div>

        <div>
          <TabsList className="mb-5" data-testid="building-blocks-tabs-list">
            <TabsTrigger
              value="included"
              data-testid="building-blocks-included-tab-trigger"
              onClick={() => {
                onTabChange && onTabChange('included');
              }}
            >
              <p className="typo-primary-500">
                {t.formatMessage(pretailoringPageMessages.lifecycleIncludedTitle, {
                  count: includedBuildingBlocks.data.length,
                })}
              </p>
            </TabsTrigger>
            <TabsTrigger
              value="excluded"
              data-testid="building-blocks-excluded-tab-trigger"
              onClick={() => {
                onTabChange && onTabChange('excluded');
              }}
            >
              <p className="typo-primary-500">
                {t.formatMessage(pretailoringPageMessages.lifecycleExcludedTitle, {
                  count: excludedBuildingBlocks.data.length,
                })}
              </p>
            </TabsTrigger>
          </TabsList>
        </div>
      </Tabs>
    </div>
  );
};
